import React, { useEffect, useRef, useState } from "react";
import FormControl from "@mui/joy/FormControl";
import FormLabel from "@mui/joy/FormLabel";
import Input from "@mui/joy/Input";
import Stack from "@mui/joy/Stack";
import CardActions from "@mui/joy/CardActions";
import CardOverflow from "@mui/joy/CardOverflow";
import "react-international-phone/style.css";
import { add, update } from "../../service/industryService";
import { Button } from "primereact/button";
import { templateAdd, templateUpdate } from "../../service/templateService";
import { Grid } from "@mui/material";
import { InputText } from "primereact/inputtext";
import MultiDropdownApi from "../../components/MultiDropdownApi";
import { Editor } from "primereact/editor";
import { QuillEditor } from "../../components/QuillEditor";
import useApiService from "../../hooks/useApiService";
import { TabPanel, TabView } from "primereact/tabview";
import GenericTableView from "../../components/GenericTableView";
import { FilterMatchMode } from "primereact/api";
import { GenericTableColumns } from "../../models/GenericTableViewModel";
import { GenericTableColumTypes, GenericTableFilterTypes } from "../../enum/TableView";
import { Avatar } from "primereact/avatar";
import { UserSession } from "../../models/UserSession";
import { Divider } from "primereact/divider";
import dayjs from "dayjs";
import DropdownApi from "../../components/DropdownApi";
import { updatePoliciesProcedure } from "../../service/formService";
import { UserTypes } from "../../enum/UserTypes";

interface PoliciesProcedureRecord {
  id: string;
  name: string;
  content: string;
  created_at: string;
  updated_at: string;
  policies_procedure_id: string;
}

export default function PoliciesProceduresSiteView(modalProps: any) {
  const record = modalProps.record;
  const session = modalProps.session as UserSession;

  const quillEditorRef = useRef<any>(null);
  const [content, setContent] = useState<string>("");
  const [lastCursorInfo, setLastCursorInfo] = useState<string>("Click in the editor to set cursor position");

  const { data, loading, error } = useApiService<PoliciesProcedureRecord>({
    method: "GET",
    url: `template/${record?.id}`,
    apiName: "core",
    onSuccess(data) {
      setContent(data.content);
    },
  });

  const [reload, setReload] = useState(false);
  const [showEditor, setShowEditor] = useState(false);
  const [employee, setEmployee] = useState<string | undefined>(undefined);
  const [employeeData, setEmployeeData] = useState<any>(null);
  const baseFilter = {
    name: { value: "", matchMode: FilterMatchMode.CONTAINS },
  };

  const columns: GenericTableColumns[] = [
    {
      field: "full_name",
      header: "Name",
      type: GenericTableColumTypes.Text,
      visible: true,
      sort: false,
      filter: true,
      filterOption: {
        type: GenericTableFilterTypes.Text,
        options: [
          {
            label: "",
            value: "",
          },
        ],
      },
    },
    {
      field: "created_at",
      header: "Signed Date",
      type: GenericTableColumTypes.Date,
      visible: true,
      sort: false,
      filter: true,
      filterOption: {
        type: GenericTableFilterTypes.Date,
        options: [
          {
            label: "",
            value: "",
          },
        ],
      },
    },
  ];


  useEffect(() => {
    if (!employeeData?.id) return;

    if (!employeeData?.signature_s3_key_photo) {
      modalProps.toast.current.show({
        severity: "error",
        summary: "No signature available for this employee",
        life: 3000,
      });
      return;
    }

  }, [employeeData]);

  const insertSignatureIntoEditor = () => {
    if (!employeeData?.signature_s3_key_photo || !quillEditorRef.current) return;

    // Just the image without any additional styling or text
    const signatureHtml = `<img width="150" style="border-bottom: 1px solid black;" src="${employeeData.signature_s3_key_photo}" alt="${employeeData.full_name}"/>`;

    // Insert the signature HTML at the last known cursor position
    quillEditorRef.current.insertHtmlAtCursor(signatureHtml);

    modalProps.toast.current.show({
      severity: "success",
      summary: "Signature inserted",
      life: 3000,
    });
  };

  // Track cursor position for user feedback
  const handleEditorFocus = () => {
    if (quillEditorRef.current?.getEditorInstance) {
      const editor = quillEditorRef.current.getEditorInstance();
      if (editor) {
        const updateCursorInfo = () => {
          const selection = editor.getSelection();
          if (selection) {
            setLastCursorInfo(`Cursor at position ${selection.index}`);
          }
        };

        editor.on('selection-change', updateCursorInfo);
        updateCursorInfo(); // Initial call
      }
    }
  };

  const saveItem = async () => {
    if (!data?.policies_procedure_id) return;
    modalProps.setGlobalLoading(true);
    await updatePoliciesProcedure(data?.policies_procedure_id, { content });
     modalProps.setGlobalLoading(false);
    modalProps.toast.current.show({
      severity: "success",
      summary: "Changes saved",
      life: 3000,
    });
    setShowEditor(false);
  };

  return (
    <React.Fragment>

      <TabView>
        <TabPanel header="Policy & Procedure">
          <div style={{ display: "flex", width: "100%" }}>
            {/* First column: Centered image */}
            <div
              style={{
                flex: 4,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "1px solid",
                minHeight: 100,
              }}
            >
              <Avatar className="p-overlay-badge" image={session.client_logo} size="xlarge" />
            </div>

            {/* Second column: 3 rows, evenly divided, full height */}
            <div
              style={{
                flex: 8,
                display: "flex",
                flexDirection: "column",
                justifyContent: "stretch",
                border: "1px solid",
                minHeight: 100,
              }}
            >
              <div style={{
                flex: 1,
                width: "100%",
                textAlign: "end",
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                borderBottom: "1px solid",
                paddingRight: 20
              }}>
                <label style={{ fontWeight: 500 }}>{session?.client_name}</label>
              </div>
              <div style={{
                flex: 1,
                width: "100%",
                textAlign: "end",
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                borderBottom: "1px solid",
                paddingRight: 20
              }}>
                <label style={{ fontWeight: 500 }}>{session?.site_name} Safety File</label>
              </div>
              <div style={{
                flex: 1,
                width: "100%",
                textAlign: "end",
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                paddingRight: 20
              }}>
                <label style={{ fontWeight: 500 }}>{record?.name}</label>
              </div>
            </div>
          </div>
          {
            showEditor ? (
              <div style={{ marginTop: 20 }}>
                <div style={{ marginBottom: '15px', padding: '10px', backgroundColor: '#f8f9fa', border: '1px solid #e9ecef', borderRadius: '6px' }}>
                  <small style={{ color: '#6c757d', fontSize: '13px', fontStyle: 'italic' }}>
                    <strong>Note:</strong> Please select an employee from the dropdown below. If the selected employee has a digital signature on file, you will be able to insert it into the document editor by clicking the "Insert Signature" button.
                  </small>
                </div>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end', marginBottom: '10px' }}>
                  <div style={{ flex: 1 }}>
                    <DropdownApi
                      apiPath="site-employee"
                      optionLabel="full_details"
                      optionValue="id"
                      label="Employee Signatures"
                      selectedData={employee}
                      setSelectedData={setEmployee}
                      setSelectedObject={setEmployeeData}
                    />
                  </div>
                  {employeeData?.signature_s3_key_photo && (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                      <Button
                        label="Insert Signature"
                        severity="info"
                        icon="pi pi-plus"
                        onClick={insertSignatureIntoEditor}
                        size="small"
                      />
                    </div>
                  )}
                </div>
                <QuillEditor
                  ref={quillEditorRef}
                  hidePlaceholder={true}
                  content={content || ""}
                  setContent={setContent}
                  label={record?.name}
                  onFocus={handleEditorFocus}
                />
              </div>
            ) : (
              <div dangerouslySetInnerHTML={{ __html: content || "" }} />
            )
          }
          <Divider />
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%", marginBottom: 16 }}>
            <div style={{ fontWeight: 500, textAlign: "left" }}>
              {record?.name}
            </div>
            <div style={{ fontWeight: 500, textAlign: "right" }}>
              Page <span>1</span> of <span>1</span>
            </div>
          </div>
          <div style={{ display: "flex", justifyContent: "flex-start", alignItems: "center", width: "100%", fontSize: 14, marginBottom: 8 }}>
            Revision: 01&nbsp;|&nbsp;
            Effective Date: {data?.created_at ? dayjs(data.created_at).format("YYYY-MM-DD") : ""}
            &nbsp;|&nbsp;
            Next Revision Date: {data?.created_at ? dayjs(data.created_at).add(1, "year").format("YYYY-MM-DD") : ""}
          </div>
        </TabPanel>
        <TabPanel header="Attendance Register">
          {
            data?.policies_procedure_id ?
              (<GenericTableView
                reload={reload}
                baseFilters={baseFilter}
                url={`site-policies-procedures-attendance/${data?.policies_procedure_id}`}
                columns={columns}
              />) : (
                <div style={{ padding: 20 }}>Policy & Procedure Still needs to be Approved.</div>
              )}
        </TabPanel>
      </TabView>
      {
        data?.policies_procedure_id && session.user_type == UserTypes.DSF && (
          <CardOverflow
            style={{ width: "100%" }}
            sx={{ borderTop: "1px solid", borderColor: "divider" }}
          >
            <CardActions
              style={{ marginTop: 15 }}
              sx={{ alignSelf: "flex-end", pt: 2 }}
            >
              {
                showEditor ? (
                  <>
                    <Button
                      type="button"
                      icon="pi pi-save"
                      label="Save"
                      className="p-button-outlined"
                      size="small"
                      style={{
                        position: "absolute",
                        fontSize: "small",
                        right: 120,
                        color: "#2dd36f",
                      }}
                      onClick={saveItem}
                    />
                    <Button
                      type="button"
                      icon="pi pi-times"
                      label="Cancel"
                      severity="danger"
                      className="p-button-outlined"
                      size="small"
                      style={{
                        position: "absolute",
                        fontSize: "small",
                        right: 0
                      }}
                      onClick={() => setShowEditor(false)}
                    />
                  </>
                ) :
                  (
                    <Button
                      type="button"
                      icon="pi pi-pencil"
                      label="Edit"
                      className="p-button-outlined"
                      size="small"
                      severity="warning"
                      style={{
                        position: "absolute",
                        fontSize: "small",
                        right: 0
                      }}
                      onClick={() => setShowEditor(true)}
                    />
                  )
              }
            </CardActions>
          </CardOverflow>
        )
      }
    </React.Fragment>
  );
}
